import round from 'helpers/mathRound';
import cronstrue from 'cronstrue';
import { getDiscount } from 'pages/quotes/utils/getDiscount';
import { extractAttributesValues, getAlleDiscount } from 'pages/quotes/helpers';


const attributesToFind = [
  'short_description',
  'additional_subscription_price',
  'quotepro_discounts',
  'image',
  'subscription_options',
  'subscription_active',
  'catalog_product_unit',
  'savings',
  'subscription_label'
];

export const calculateTotals = (items) => {
  const totals = {
    discount: 0,
    subtotal: 0,
    total: 0,
  };

  const skuTotals = items.reduce((acc, item) => {
    const {
      sku,
      qty,
      product_option: {
        extension_attributes: {
          subscription_id,
          quotepro_discounts: customQuoteproDiscounts
        },
      },
      price: updatedPrice
    } = item;

    const attributesValues = extractAttributesValues(item.custom_attributes, attributesToFind);
    const {
      subscription_options,
      subscription_active,
      savings,
      quotepro_discounts
    } = attributesValues || {};

    const savingsValue = round(Number(savings)) || 0;

    const itemPrice = updatedPrice || item.price;

    const dataForDiscount = {
      savings: savingsValue,
      discount_options: quotepro_discounts ? JSON.parse(quotepro_discounts) : [customQuoteproDiscounts],
      itemPrice
    };

    const isAlleSKU = item.sku.includes('bdsku');

    const discount = isAlleSKU
      ? round(getAlleDiscount({ sku, name: item.name })) || 0
      : round(getDiscount(item, dataForDiscount)) || 0;

    const subscriptionData = subscription_options?.find((s) => s.id === subscription_id);
    const isNullPrice = subscriptionData?.installment_price === null && subscriptionData?.adjustment_price === null;

    const regularPrice = !!subscriptionData && subscription_active === '1' && !isNullPrice && updatedPrice === 0
      ? round(subscriptionData.installment_price + subscriptionData.adjustment_price)
      : itemPrice || 0;

    const subtotal = regularPrice * qty;
    const totalAmount = savingsValue && !updatedPrice
      ? (regularPrice * qty)
      : (regularPrice * qty) - discount;

    totals.discount += discount;
    totals.subtotal += subtotal;
    totals.total += totalAmount;

    acc[sku] = {
      discount,
      subtotal,
      total: totalAmount,
    };

    return acc;
  }, {});

  return {
    skuTotals,
    totals,
  };
};

const getLocalTimeFromCron = (cronExpression: string): string => {
  try {
    const parts = cronExpression.split(' ');

    if (parts.length < 2) {
      throw new Error('Invalid cron expression');
    }

    const utcHour = parseInt(parts[1], 10);
    const utcMinute = parseInt(parts[0], 10);

    const now = new Date();
    const utcDate = new Date(Date.UTC(
      now.getFullYear(),
      now.getMonth(),
      now.getDate(),
      utcHour,
      utcMinute
    ));
    return new Intl.DateTimeFormat('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    }).format(utcDate);
  } catch (error) {
    return 'Invalid time';
  }
};

export const cronToHumanReadable = (cronExpression) => {
  let readableTime = 'Invalid cron expression';

  try {
    readableTime = cronstrue.toString(cronExpression, {
      use24HourTimeFormat: false,
      verbose: true,
    });
    const localTime = getLocalTimeFromCron(cronExpression);
    readableTime = readableTime.replace(/\d{1,2}:\d{2} [APMapm]{2}/, localTime);
  } catch (error) {
    readableTime = 'Invalid cron expression';
  }

  return readableTime;
};

export const defaultCustomerTableCells = [
  {
    label: 'First Name',
    display: true,
    width: 100,
    sortField: 'firstName'
  },
  {
    label: 'Last Name',
    display: true,
    width: 100,
    sortField: 'lastName'
  },
  {
    label: 'Customer Email',
    display: true,
    width: 160,
    sortField: 'email'
  },
  {
    label: 'Phone',
    display: false,
    width: 120,
    sortField: 'phone'
  },
  {
    label: 'Reward Points',
    display: false,
    width: 100,
    sortField: 'rewardPoints'
  },
  {
    label: 'Date of Birth',
    display: false,
    width: 100,
    sortField: 'dob'
  },
  {
    label: 'Last Purchase Date',
    display: true,
    width: 100,
    sortField: 'lastPayment'
  },
  {
    label: 'Total Spent',
    display: true,
    width: 80,
    sortField: 'totalSpend'
  }
];

export const defaultScheduleTableCells = [
  {
    label: 'Quote Name',
    display: true,
    width: 100,
    align: 'left'
  },
  {
    label: 'Start Date',
    display: false,
    width: 100,
    align: 'left',
  },
  {
    label: 'Expiration Date',
    display: true,
    width: 100,
    align: 'left'
  },
  {
    label: 'Must Used Date',
    display: false,
    width: 100,
    align: 'left'
  },
  {
    label: 'Quote Discount',
    display: true,
    width: 100,
    align: 'center'
  },
  {
    label: 'Period',
    display: true,
    width: 100,
    align: 'left'
  },
  {
    label: 'Last Run',
    display: false,
    width: 100,
    align: 'left'
  },
  {
    label: 'Selected Rule',
    display: false,
    width: 100,
    align: 'left'
  },
  {
    label: 'Run Immediately',
    display: true,
    width: 80,
    align: 'center'
  },
  {
    label: 'Is Active',
    display: true,
    width: 80,
    align: 'center'
  },
  {
    label: 'Created By',
    display: false,
    width: 180,
    align: 'left'
  },
];

export const dayRanges = [
  { label: '1 day', value: 1 },
  { label: '7 days', value: 7 },
  { label: '30 days', value: 30 },
  { label: '90 days', value: 90 },
  { label: '180 days', value: 180 },
  { label: '365 days', value: 365 },
];
